import { useCallback, useState } from "react";
import { useMutation } from "urql";
import { graphql } from "@acto/gql";
import type { MeetingCancellationReason } from "@acto/gql/graphql";
import type { Meeting } from "@acto/taskpane/core/domain";

const CancelMeetingsMutation = graphql(`
  mutation CancelMeetings($input: CancelMeetingsInput!) {
    cancelMeetings(input: $input) {
      id
      __typename
      scheduledDate
      scheduledStartTime
      scheduledEndTime
      occurrenceDate
      client {
        id
        legacyId
        name
        externalReference
        __typename
      }
    }
  }
`);

/**
 * How a report is addressed for cancellation. A recurring occurrence may have no meeting row yet, and
 * its id is derived from `(seriesId, occurrenceDate)` and cannot be reversed, so the pair is sent
 * instead of the id.
 */
export function cancellationTargetFor(meeting: Meeting) {
  return meeting.series && meeting.occurrenceDate
    ? { seriesId: meeting.series.id, occurrenceDate: meeting.occurrenceDate.format("YYYY-MM-DD") }
    : { meetingId: meeting.id };
}

export function useCancelMeetings() {
  const [result, cancelMeetingsMutation] = useMutation(CancelMeetingsMutation);
  const [error, setError] = useState<Error | null>(null);

  const cancelMeetings = useCallback(
    async (meetings: Meeting[], reason: MeetingCancellationReason, comment?: string) => {
      const trimmedComment = comment?.trim();

      try {
        const response = await cancelMeetingsMutation({
          input: {
            targets: meetings.map(cancellationTargetFor),
            reason,
            comment: trimmedComment ? trimmedComment : undefined,
          },
        });
        if (response.error) {
          throw response.error;
        }
        return response.data?.cancelMeetings;
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err);
          return null;
        } else {
          throw err;
        }
      }
    },
    [cancelMeetingsMutation],
  );

  return { cancelMeetings, result, error };
}
